import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, from, throwError, catchError, filter, switchMap, take } from 'rxjs';
import { NavigationService } from 'src/app/services/navigation.service';
import { AuthService } from 'src/app/services/auth.service';

import { LocalStorageService } from './local-storage.service';
import { EnvironmentService } from './environment/environment.service';
import { AuthSessionIntegrityService } from './auth-session-integrity.service';
import { tryGetErrorMessage } from 'src/app/shared/utils/error-utils';
import {
  authSessionOwnership,
  AuthSessionOwnership,
  hasStoredAuth,
  isTokenAboutToExpire,
  stillOwnsSession,
} from 'src/app/shared/utils/auth-token-utils';
import {
  AuthRefreshCoordinatorService,
  AuthRefreshResponseError,
  AuthSessionSupersededError,
  AuthRefreshUnavailableError,
} from './auth-refresh-coordinator.service';

const TOKEN_HEADER_KEY = "Bearer";

const TOKEN_EXPIRATION_ERROR = 'Token Expiration';

const ANONYMOUS_URL_PATTERNS = [
  '/device/authorize',
  '/device/token'
];

// `owner` is the session stored when the refresh settled, so the leading request can tell whether it
// changed before it resumed.
type RefreshOutcome =
  | { kind: 'token'; token: string; owner: AuthSessionOwnership }
  | { kind: 'stale' }
  | { kind: 'error'; error: unknown }
  | { kind: 'transient'; error: unknown; owner: AuthSessionOwnership }
  | { kind: 'fallback'; owner: AuthSessionOwnership };

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private _isRefreshing = false;
  private _refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // Emitted to release queued refresh-waiters when this tab goes stale mid-refresh (works whether or not a
  // new JWT exists, e.g. when the refresh itself failed). Queued requests treat it as "abort".
  private readonly _staleRefreshSignal = Symbol('stale-refresh');
  private readonly _failedRefreshSignal = Symbol('failed-refresh');

  constructor(
    private readonly _localStorage: LocalStorageService,
    private readonly _navigationService: NavigationService,
    private readonly _authService: AuthService,
    private readonly _environmentService: EnvironmentService,
    private readonly _sessionIntegrity: AuthSessionIntegrityService,
    private readonly _authRefreshCoordinator: AuthRefreshCoordinatorService) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isAnonymousUrl = ANONYMOUS_URL_PATTERNS.some(pattern => request.url.includes(pattern));

    if (isAnonymousUrl) {
      return next.handle(request);
    }

    // If another tab changed this tab's authenticated identity, this tab is stale: block every
    // authenticated request (before any token refresh or token attach) so a queued/debounced call can't
    // write under the wrong identity. The user is shown a reload prompt by AuthSessionIntegrityService.
    if (this._sessionIntegrity.isStale) {
      return this.staleSessionError(request.url);
    }

    const authData = this._localStorage.authorizationData;
    const sentOwner = authSessionOwnership(authData);
    if (!request.url.includes('refreshToken') && hasStoredAuth(authData) && isTokenAboutToExpire(authData)) {
      return this.refreshToken(request, next, undefined, sentOwner);
    }

    const sentToken = authData?.token;
    if (authData && authData.token) {
      request = this.addTokenHeader(request, authData.token);
    }

    return next.handle(request).pipe(
      catchError(error => {
        // If the tab went stale while this request was in flight, don't run clearAll() on a 401 — that would
        // null out the other tab's new identity in shared localStorage. Surface the stale error instead.
        if (this._sessionIntegrity.isStale) {
          return this.staleSessionError(request.url);
        }
        if (error instanceof HttpErrorResponse && error.error === TOKEN_EXPIRATION_ERROR) {
          return this.refreshToken(request, next, sentToken, sentOwner);
        } else if (error.status === 401 && this.mayInvalidate(sentOwner)) {
          this._authService.clearAll();
          this._navigationService.navigateToLogin();
        }
        error.currentPath = location.pathname;
        error.message = tryGetErrorMessage(error) || null;
        throw error;
      })
    );
  }

  private addTokenHeader = (request: HttpRequest<any>, token: string) => {
    return request.clone({
      setHeaders: {
        Authorization: `${TOKEN_HEADER_KEY} ${token}`,
        'le-frontend-build-version': this._environmentService.version
      }
    });
  }

  private staleSessionError = (url: string): Observable<never> =>
    throwError(() => new HttpErrorResponse({
      status: 0,
      statusText: 'Session changed in another tab',
      url,
    }));

  // Called the moment a refresh resolves/errors while this tab is stale, BEFORE any persist/clear/navigate
  // side effect — so we never write superseded auth back into shared localStorage. Resets the refresh flag
  // and releases queued waiters (via the sentinel) so they error out cleanly instead of hanging.
  private failRefreshForStale = (): void => {
    this._isRefreshing = false;
    this._refreshTokenSubject.next(this._staleRefreshSignal);
  };

  private failRefreshForWaiters = (): void => {
    this._isRefreshing = false;
    this._refreshTokenSubject.next(this._failedRefreshSignal);
  };

  private failedRefreshError = (url: string): Observable<never> =>
    throwError(() => new HttpErrorResponse({
      status: 0,
      statusText: 'Token refresh failed',
      url,
    }));

  // A refresh that timed out, never reached the server or hit a server fault says nothing about whether the
  // session is valid: keep the stored auth, and fail the request instead of resending it with the old token.
  private isTransientRefreshFailure = (error: any): boolean =>
    error?.name === 'TimeoutError'
    || (error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500));

  // Destructive side effects only have to prove they are not destroying *someone else's* session: with
  // nothing stored there is nothing to destroy, and an anonymous/cleared session must still redirect.
  private mayInvalidate = (owner: AuthSessionOwnership): boolean => {
    const current = this._localStorage.authorizationData;
    return !hasStoredAuth(current) || stillOwnsSession(owner, current);
  };

  // Runs the refresh to completion whether or not the request that started it is still subscribed. The
  // in-flight flag, the waiter release and the clear/navigate decision live in this promise rather than in
  // operators on the leading request's observable, because those operators never run once that request
  // unsubscribes, which left the flag set for the life of the tab. The flag stays set until the coordinator
  // settles, so a later expiry queues behind this refresh instead of starting an overlapping one.
  private settleRefresh = (
    rejectedToken: string | undefined,
    refreshOwner: AuthSessionOwnership
  ): Promise<RefreshOutcome> =>
    this._authRefreshCoordinator.getUsableAccessToken(rejectedToken, refreshOwner).then(
      (token): RefreshOutcome => {
        // Another tab may have swapped the identity during the async refresh. Check BEFORE releasing the
        // refreshed token so no request is sent under the wrong identity.
        if (this._sessionIntegrity.isStale) {
          this.failRefreshForStale();
          return { kind: 'stale' };
        }
        const owner = authSessionOwnership(this._localStorage.authorizationData);
        this._isRefreshing = false;
        this._refreshTokenSubject.next(token);
        return { kind: 'token', token, owner };
      },
      (error): RefreshOutcome => {
        // Stale check first: if the identity was swapped mid-refresh, don't clearAll() (which would null
        // out the other tab's new auth in shared localStorage) and don't resend the original request.
        if (this._sessionIntegrity.isStale) {
          this.failRefreshForStale();
          return { kind: 'stale' };
        }
        this.failRefreshForWaiters();
        if (error instanceof AuthSessionSupersededError) {
          return { kind: 'error', error };
        }
        if (error instanceof AuthRefreshResponseError || error instanceof AuthRefreshUnavailableError) {
          if (this.mayInvalidate(refreshOwner)) {
            this._authService.clearAll();
            this._navigationService.navigateToLogin();
          }
          return { kind: 'error', error };
        }
        if (error?.error !== TOKEN_EXPIRATION_ERROR && (error?.status === 401 || error?.status === 403)) {
          if (this.mayInvalidate(refreshOwner)) {
            this._authService.clearAll();
            this._navigationService.navigateToLogin();
          }
          return { kind: 'error', error };
        }
        if (this.isTransientRefreshFailure(error)) {
          return { kind: 'transient', error, owner: authSessionOwnership(this._localStorage.authorizationData) };
        }
        return { kind: 'fallback', owner: authSessionOwnership(this._localStorage.authorizationData) };
      });

  private refreshToken = (
    request: HttpRequest<any>,
    next: HttpHandler,
    rejectedToken?: string,
    originOwner?: AuthSessionOwnership
  ) => {
    if (!this._isRefreshing) {
      this._isRefreshing = true;
      this._refreshTokenSubject.next(null);
      const refreshOwner = originOwner !== undefined
        ? originOwner
        : authSessionOwnership(this._localStorage.authorizationData);

      return from(this.settleRefresh(rejectedToken, refreshOwner)).pipe(
        switchMap(outcome => {
          if (outcome.kind === 'stale') {
            return this.staleSessionError(request.url);
          }
          if (outcome.kind === 'error') {
            return throwError(() => outcome.error);
          }
          // This request resumes a microtask after the refresh settles, and the session can change in that
          // gap: another tab marks this one stale, while a same-tab logout, relogin or impersonation does not.
          if (this._sessionIntegrity.isStale) {
            return this.staleSessionError(request.url);
          }
          if (!stillOwnsSession(outcome.owner, this._localStorage.authorizationData)) {
            return throwError(() => new AuthSessionSupersededError());
          }
          if (outcome.kind === 'transient') {
            return throwError(() => outcome.error);
          }
          if (outcome.kind === 'fallback') {
            return next.handle(request);
          }
          return next.handle(this.addTokenHeader(request, outcome.token));
        })
      );
    }
    const waiterOwner = originOwner ?? authSessionOwnership(this._localStorage.authorizationData);
    return this._refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => {
        // A queued request can outlive a cross-tab identity change: abort on the stale sentinel or if the
        // tab has since gone stale, rather than attaching a token under the wrong identity.
        if (token === this._staleRefreshSignal || this._sessionIntegrity.isStale) {
          return this.staleSessionError(request.url);
        }
        if (token === this._failedRefreshSignal) {
          return this.failedRefreshError(request.url);
        }
        if (!stillOwnsSession(waiterOwner, this._localStorage.authorizationData)) {
          return throwError(() => new AuthSessionSupersededError());
        }
        return next.handle(this.addTokenHeader(request, token));
      })
    );
  }

}
